import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Package, ThumbsUp, ThumbsDown, X, CheckCircle2 } from "lucide-react";
import { getPendingPurchases, confirmPurchase, cancelPurchase, type PendingPurchase } from "@/lib/intentCapture";
import { addToHistory } from "@/lib/watchlistStore";

export default function PurchaseFollowUp() {
  const pending = getPendingPurchases();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [completed, setCompleted] = useState<Set<string>>(new Set());

  if (pending.length === 0) return null;

  const visible = pending.filter((p) => !dismissed.has(p.id) && !completed.has(p.id));
  if (visible.length === 0) return null;

  const handleConfirm = (p: PendingPurchase) => {
    confirmPurchase(p.id);
    addToHistory({
      query: `Purchased: ${p.productName}`,
      category: "",
      winnerName: p.productName,
      winnerPrice: p.price,
      winnerLabel: "Purchased",
      confidence: "high",
      coverage: "live",
    });
    setCompleted((s) => new Set(s).add(p.id));
  };

  const handleDeny = (p: PendingPurchase) => {
    cancelPurchase(p.id);
    setDismissed((s) => new Set(s).add(p.id));
  };

  const handleDismiss = (id: string) => {
    setDismissed((s) => new Set(s).add(id));
  };

  return (
    <div className="space-y-2">
      {visible.slice(0, 2).map((p) => (
        <motion.div
          key={p.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-border bg-card/60 px-4 py-3"
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <Package className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs text-muted-foreground font-body">Did you buy this?</span>
            </div>
            <button onClick={() => handleDismiss(p.id)} className="p-0.5">
              <X className="w-3 h-3 text-muted-foreground" />
            </button>
          </div>
          <p className="text-sm font-body font-medium text-foreground mb-1">{p.productName}</p>
          <p className="text-xs text-muted-foreground font-body mb-3">
            {p.price} via {p.seller}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => handleConfirm(p)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-body bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              <ThumbsUp className="w-3 h-3" />Yes, bought it
            </button>
            <button
              onClick={() => handleDeny(p)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-body text-muted-foreground hover:bg-muted/50 transition-colors border border-border/50"
            >
              <ThumbsDown className="w-3 h-3" />No
            </button>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
